import { useEffect, useRef, useState } from 'react';
import { X, ArrowLeft, Loader2, Mail, Lock, Eye, EyeOff, Smartphone, MailCheck, ExternalLink } from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useLanguage } from '@/hooks/useLanguage';
import { isInAppBrowser, isAndroid, isIOS, buildAndroidChromeIntent } from '@/lib/userAgentDetection';

// ─── Migration notice ─────────────────────────────────────────────────────────
// Set to null once all pre-launch users have migrated.
const MIGRATION_LIVE_DATE = null;
// ─────────────────────────────────────────────────────────────────────────────

type AuthMode = 'choose' | 'signin' | 'signup' | 'reset' | 'resetSent' | 'otp' | 'otpSent';

export type AuthDismissReason = 'dismissed' | 'awaiting-email';

interface AuthModalProps {
  isOpen: boolean;
  /** Closed without signing in. 'awaiting-email' means a link or code was just sent, so work waiting on sign-in should be kept. */
  onClose: (reason?: AuthDismissReason) => void;
  /** Signed in from inside the modal. Defaults to onClose for callers that don't distinguish. */
  onSuccess?: () => void;
  onSignIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  onSignUp: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  onGoogleSignIn?: () => Promise<{ success: boolean; error?: string }>;
  onResetPassword?: (email: string) => Promise<{ success: boolean; error?: string }>;
  /** Sends a magic-link / OTP email. When provided, an "Email me a sign-in
   *  link" option is shown. Critical fallback for in-app webview users
   *  where Google OAuth is blocked. */
  onEmailOtp?: (email: string) => Promise<{ success: boolean; error?: string }>;
  /** Signs in with the one-time code from that same email. */
  onVerifyEmailOtp?: (email: string, token: string) => Promise<{ success: boolean; error?: string }>;
}

export function AuthModal({ isOpen, onClose, onSuccess, onSignIn, onSignUp, onGoogleSignIn, onResetPassword, onEmailOtp, onVerifyEmailOtp }: AuthModalProps) {
  const { t } = useLanguage();
  const [mode, setMode] = useState<AuthMode>('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [installApp, setInstallApp] = useState(true);
  const { promptInstall, isInstallable } = usePWAInstall();
  const dialogRef = useRef<HTMLDivElement>(null);
  const dismissRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!isOpen) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismissRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setInstallApp(true);
    setOtpCode('');
  };

  const handleBack = () => {
    resetForm();
    setMode('choose');
  };

  const handleClose = () => {
    const reason: AuthDismissReason = mode === 'otpSent' || mode === 'resetSent' ? 'awaiting-email' : 'dismissed';
    resetForm();
    setMode('choose');
    onClose(reason);
  };
  dismissRef.current = handleClose;

  const handleSuccess = () => {
    resetForm();
    setMode('choose');
    if (onSuccess) onSuccess(); else onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t.auth.pleaseEnterEmail);
      return;
    }

    if (!password) {
      setError(t.auth.pleaseEnterPassword);
      return;
    }

    if (mode === 'signup') {
      if (password.length < 6) {
        setError(t.auth.passwordTooShort);
        return;
      }

      if (password !== confirmPassword) {
        setError(t.auth.passwordsDoNotMatch);
        return;
      }
    }

    setLoading(true);

    try {
      const result = mode === 'signin'
        ? await onSignIn(email, password)
        : await onSignUp(email, password);

      if (result.success) {
        if (mode === 'signup' && installApp && isInstallable) {
          await promptInstall();
        }
        handleSuccess();
      } else {
        setError(result.error || t.auth.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        role="presentation"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="auth-modal-title" className="relative w-full max-w-sm max-h-[90dvh] overflow-y-auto overscroll-contain outline-none rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            {mode !== 'choose' && mode !== 'resetSent' && (
              <button
                onClick={handleBack}
                aria-label="Go back"
                className="p-1.5 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h2 id="auth-modal-title" className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
              {mode === 'choose' && t.common.signIn}
              {mode === 'signin' && t.auth.welcomeBack}
              {mode === 'signup' && t.auth.createAccount}
              {mode === 'reset' && 'Reset password'}
              {mode === 'resetSent' && 'Email sent'}
              {mode === 'otp' && t.auth.signInLinkTitle}
              {mode === 'otpSent' && t.auth.emailSent}
            </h2>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="p-2 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {mode === 'choose' && (
            <div className="space-y-3">
              {error && (
                <div role="alert" className="p-3 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/20 text-[var(--status-red)] text-sm">
                  {error}
                </div>
              )}
              {MIGRATION_LIVE_DATE && (
                <div className="p-3 rounded-lg bg-amber-500/25 border border-amber-500/40 text-[var(--accent-gold)] text-xs text-center leading-relaxed">
                  👋 Beta users who registered before {MIGRATION_LIVE_DATE} will need to create a new account — sorry for the inconvenience!
                </div>
              )}
              <p className="text-sm text-[var(--text-secondary)] text-center mb-2">
                {t.auth.signInDescription}
              </p>
              <p className="text-xs text-[var(--text-secondary)]/60 text-center mb-4">
                {t.auth?.apiCostNote || "TripBoom uses premium APIs to find your perfect places — it's free to use, but please consider going Pro to help with our running and API costs."}
              </p>

              {isInAppBrowser() && (
                <>
                  {/* In-app webview detected (Facebook, Instagram, WhatsApp,
                      LinkedIn, Slack, etc.). Google blocks OAuth in these
                      via "disallowed_useragent". Offer working alternatives:
                      magic-link email (works everywhere) + platform escape
                      to a real browser for users who specifically want Google. */}
                  {onEmailOtp && (
                    <button
                      onClick={() => setMode('otp')}
                      disabled={loading}
                      aria-label={t.auth.emailSignInLink}
                      className="w-full py-3 px-4 rounded-xl bg-[var(--accent-teal)] text-white font-medium hover:bg-[var(--accent-teal)]/90 transition-colors flex items-center justify-center gap-2"
                    >
                      <Mail className="w-5 h-5" />
                      {t.auth.emailSignInLink}
                    </button>
                  )}

                  {onGoogleSignIn && isAndroid() && (
                    <button
                      onClick={() => {
                        window.location.replace(buildAndroidChromeIntent(window.location.href));
                      }}
                      aria-label="Open in Chrome to use Google sign-in"
                      className="w-full py-3 px-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] font-medium hover:bg-[var(--bg-elevated)]/80 hover:border-[var(--accent-teal)]/40 transition-colors flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open in Chrome to use Google
                    </button>
                  )}

                  {onGoogleSignIn && isIOS() && (
                    <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] text-xs text-[var(--text-secondary)] leading-relaxed">
                      <p className="font-semibold text-[var(--text-primary)] mb-1 flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5" />
                        Want to use Google sign-in?
                      </p>
                      <p>
                        Tap <strong>•••</strong> (or the share menu) at the bottom-right of this in-app browser, then choose "Open in Safari". Google sign-in works there.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-[var(--border)]" />
                    <span className="text-xs text-[var(--text-secondary)]">or</span>
                    <div className="flex-1 h-px bg-[var(--border)]" />
                  </div>
                </>
              )}

              {onGoogleSignIn && !isInAppBrowser() && (
                <>
                  <button
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const result = await onGoogleSignIn();
                        if (!result.success) setError(result.error || t.auth.somethingWentWrong);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    aria-label="Continue with Google"
                    className="w-full py-3 px-4 rounded-xl bg-white text-gray-800 font-medium border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center gap-3"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </button>

                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-[var(--border)]" />
                    <span className="text-xs text-[var(--text-secondary)]">or</span>
                    <div className="flex-1 h-px bg-[var(--border)]" />
                  </div>
                </>
              )}

              <button
                onClick={() => setMode('signin')}
                aria-label="I have an account"
                className="w-full py-3 px-4 rounded-xl bg-[var(--accent-teal)] text-white font-medium hover:bg-[var(--accent-teal)]/90 transition-colors"
              >
                {t.auth.iHaveAccount}
              </button>

              <button
                onClick={() => setMode('signup')}
                aria-label="I'm new here"
                className="w-full py-3 px-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] font-medium hover:bg-[var(--bg-elevated)]/80 hover:border-[var(--accent-teal)]/40 transition-colors"
              >
                {t.auth.imNewHere}
              </button>
            </div>
          )}

          {(mode === 'signin' || mode === 'signup') && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/40 text-[var(--status-red)] text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs text-[var(--text-secondary)] font-medium">{t.common.email}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.auth.emailPlaceholder}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-[var(--text-secondary)] font-medium">{t.common.password}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? t.auth.passwordMinChars : t.auth.passwordPlaceholder}
                    className="w-full pl-10 pr-10 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {mode === 'signup' && (
                <>
                  <div className="space-y-1">
                    <label className="text-xs text-[var(--text-secondary)] font-medium">{t.common.confirmPassword}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t.auth.confirmPasswordPlaceholder}
                        className="w-full pl-10 pr-10 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                        aria-pressed={showConfirmPassword}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {isInstallable && (
                    <label className="flex items-center gap-3 p-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] cursor-pointer hover:border-[var(--accent-teal)]/40 hover:bg-[var(--accent-teal)]/5 hover:shadow-md transition-all">
                      <input
                        type="checkbox"
                        checked={installApp}
                        onChange={(e) => setInstallApp(e.target.checked)}
                        className="w-4 h-4 rounded border-[var(--border)] bg-[var(--bg-primary)] text-[var(--accent-teal)] focus:ring-[var(--accent-teal)] focus:ring-offset-0"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <Smartphone className="w-4 h-4 text-[var(--accent-teal)]" />
                        <span className="text-sm text-[var(--text-primary)]">{t.auth.installWebApp}</span>
                      </div>
                    </label>
                  )}
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                aria-label={mode === 'signin' ? 'Sign in' : 'Create account'}
                className="w-full py-3 rounded-xl font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {mode === 'signin' ? t.auth.signingIn : t.auth.creatingAccount}
                  </>
                ) : (
                  mode === 'signin' ? t.common.signIn : t.auth.createAccount
                )}
              </button>

              {mode === 'signin' && onResetPassword && (
                <p className="text-xs text-center">
                  <button
                    type="button"
                    onClick={() => { resetForm(); setMode('reset'); }}
                    aria-label="Forgot password"
                    className="text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors"
                  >
                    Forgot password?
                  </button>
                </p>
              )}

              <p className="text-xs text-center text-[var(--text-secondary)]">
                {mode === 'signin' ? (
                  <>
                    {t.auth.newHere}{' '}
                    <button
                      type="button"
                      onClick={() => { resetForm(); setMode('signup'); }}
                      aria-label="Create an account"
                      className="text-[var(--accent-teal)] hover:underline"
                    >
                      {t.auth.createAnAccount}
                    </button>
                  </>
                ) : (
                  <>
                    {t.auth.alreadyHaveAccount}{' '}
                    <button
                      type="button"
                      onClick={() => { resetForm(); setMode('signin'); }}
                      aria-label="Sign in"
                      className="text-[var(--accent-teal)] hover:underline"
                    >
                      {t.common.signIn}
                    </button>
                  </>
                )}
              </p>

              {onEmailOtp && (
                <button
                  type="button"
                  onClick={() => { setError(null); setPassword(''); setMode('otp'); }}
                  className="w-full py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--text-primary)] hover:border-[var(--accent-teal)]/40 hover:bg-[var(--bg-elevated)] transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4 text-[var(--accent-teal)]" />
                  {t.auth.emailSignInLinkInstead}
                </button>
              )}
            </form>
          )}

          {mode === 'reset' && onResetPassword && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!email.trim()) { setError(t.auth.pleaseEnterEmail); return; }
                setLoading(true);
                setError(null);
                try {
                  const result = await onResetPassword(email);
                  if (result.success) {
                    setResetEmail(email);
                    setMode('resetSent');
                  } else {
                    setError(result.error || t.auth.somethingWentWrong);
                  }
                } finally {
                  setLoading(false);
                }
              }}
              className="space-y-4"
            >
              {error && (
                <div className="p-3 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/20 text-[var(--status-red)] text-sm">
                  {error}
                </div>
              )}
              <p className="text-sm text-[var(--text-secondary)]">
                Enter your email and we'll send you a link to reset your password.
              </p>
              <div className="space-y-1">
                <label className="text-xs text-[var(--text-secondary)] font-medium">{t.common.email}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.auth.emailPlaceholder}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                aria-label="Send reset link"
                className="w-full py-3 rounded-xl font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)' }}
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send reset link'}
              </button>
            </form>
          )}

          {mode === 'resetSent' && (
            <div className="space-y-5 py-2 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-[var(--accent-teal)]/15 flex items-center justify-center">
                  <MailCheck className="w-8 h-8 text-[var(--accent-teal)]" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-[var(--text-primary)] font-medium">Reset link sent</p>
                <p className="text-sm font-mono text-[var(--accent-teal)] break-all">{resetEmail}</p>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed pt-1">
                  Check your email for a password reset link. Check spam if you don't see it.
                </p>
              </div>
              <button
                onClick={handleClose}
                aria-label="Done"
                className="w-full py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] font-medium hover:border-[var(--accent-teal)]/40 transition-colors"
              >
                Done
              </button>
            </div>
          )}

          {mode === 'otp' && onEmailOtp && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!email.trim()) { setError(t.auth.pleaseEnterEmail); return; }
                setLoading(true);
                setError(null);
                try {
                  const result = await onEmailOtp(email);
                  if (result.success) {
                    setResetEmail(email);
                    setOtpCode('');
                    setMode('otpSent');
                  } else {
                    setError(result.error || t.auth.somethingWentWrong);
                  }
                } finally {
                  setLoading(false);
                }
              }}
              className="space-y-4"
            >
              {error && (
                <div className="p-3 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/20 text-[var(--status-red)] text-sm">
                  {error}
                </div>
              )}
              <p className="text-sm text-[var(--text-secondary)]">
                {t.auth.signInLinkDescription}
              </p>
              <div className="space-y-1">
                <label className="text-xs text-[var(--text-secondary)] font-medium">{t.common.email}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.auth.emailPlaceholder}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                aria-label={t.auth.sendSignInLink}
                className="w-full py-3 rounded-xl font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)' }}
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.auth.sending}</> : t.auth.sendSignInLink}
              </button>
            </form>
          )}

          {mode === 'otpSent' && (
            <div className="space-y-5 py-2 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-[var(--accent-teal)]/15 flex items-center justify-center">
                  <MailCheck className="w-8 h-8 text-[var(--accent-teal)]" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-[var(--text-primary)] font-medium">{t.auth.checkYourEmail}</p>
                <p className="text-sm font-mono text-[var(--accent-teal)] break-all">{resetEmail}</p>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed pt-1">
                  {onVerifyEmailOtp ? t.auth.signInLinkOrCodeBody : t.auth.signInLinkSentBody}
                </p>
              </div>
              {onVerifyEmailOtp && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const code = otpCode.replace(/\D/g, '');
                    if (code.length !== 6) { setError(t.auth.enterCodeFromEmail); return; }
                    setLoading(true);
                    setError(null);
                    try {
                      const result = await onVerifyEmailOtp(resetEmail, code);
                      if (result.success) {
                        handleSuccess();
                      } else {
                        setError(/expired|invalid/i.test(result.error ?? '') ? t.auth.codeInvalid : (result.error || t.auth.somethingWentWrong));
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="space-y-3 text-left"
                >
                  {error && (
                    <div className="p-3 rounded-lg bg-[var(--status-red-bg)] border border-[var(--status-red)]/20 text-[var(--status-red)] text-sm">
                      {error}
                    </div>
                  )}
                  <label htmlFor="otp-code" className="text-xs text-[var(--text-secondary)] font-medium">{t.auth.signInCodeLabel}</label>
                  <input
                    id="otp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={12}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] text-center font-mono text-lg tracking-[0.3em] placeholder-[var(--text-secondary)]/30 focus:outline-none focus:border-[var(--accent-teal)] transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    aria-label={t.auth.signInWithCode}
                    className="w-full py-3 rounded-xl font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)' }}
                  >
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.auth.checkingCode}</> : t.auth.signInWithCode}
                  </button>
                </form>
              )}
              <button
                onClick={handleClose}
                aria-label="Done"
                className="w-full py-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] font-medium hover:border-[var(--accent-teal)]/40 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
